import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import {
  buildSubscriptionPackages,
  getSubscriptionPackage,
  hasPaymentSettingPricing,
  RenewalPricingSource,
  SubscriptionPackage,
  SubscriptionPackageId,
} from "../../../constants/subscription-packages.constant";
import { PaymentSettingService } from "../../../services/payment-setting.service";
import { PreviewAccessService } from "../../../services/preview-access.service";
import { PreviewTempDataService } from "../../../services/preview-temp-data.service";

export { SubscriptionPackageId } from "../../../constants/subscription-packages.constant";

@Component({
  selector: "app-preview-subscription",
  templateUrl: "./preview-subscription.component.html",
  styleUrls: ["./preview-subscription.component.scss"],
})
export class PreviewSubscriptionComponent implements OnInit {
  previewId = "";
  previewName = "digital-memory";
  selectedPackageId: SubscriptionPackageId = "year";
  processing = false;
  loading = true;
  loadError: string | null = null;
  packages: SubscriptionPackage[] = [];
  private pricingSource: RenewalPricingSource = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private paymentSettingService: PaymentSettingService,
    private previewTempDataService: PreviewTempDataService,
    private previewAccessService: PreviewAccessService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.previewId = params["previewId"] || "";
      this.previewName = params["previewName"] || "digital-memory";
      this.loadRenewalPricing();
    });

    this.route.queryParams.subscribe(queryParams => {
      const packageId = queryParams["package"];
      if (packageId === "month" || packageId === "year") {
        this.selectedPackageId = packageId;
      }
    });
  }

  selectPackage(packageId: SubscriptionPackageId): void {
    this.selectedPackageId = packageId;
  }

  get selectedPackage(): SubscriptionPackage | undefined {
    return getSubscriptionPackage(this.selectedPackageId, this.pricingSource);
  }

  onSubscribe(): void {
    if (!this.previewId || this.processing || this.loading) {
      return;
    }

    if (!this.selectedPackage) {
      return;
    }

    this.processing = true;
    this.router
      .navigate([`preview-${this.previewName}-${this.previewId}`, "subscription", "checkout"], {
        queryParams: { package: this.selectedPackageId },
      })
      .then(success => {
        this.processing = false;
        if (!success) {
          this.toastr.error("Unable to open checkout page.", "Error");
        }
      })
      .catch(() => {
        this.processing = false;
        this.toastr.error("Unable to open checkout page.", "Error");
      });
  }

  goBack(): void {
    if (!this.previewId) {
      this.router.navigate(["/"]);
      return;
    }

    this.router.navigate([`preview-${this.previewName}-${this.previewId}`]);
  }

  retry(): void {
    this.loadRenewalPricing();
  }

  /**
   * Prefer ready paymentSetting from preview cache.
   * If missing, GET /PaymentSettings/digitalMemoryLog/{digitalMemoryLogId} (public).
   */
  private loadRenewalPricing(): void {
    this.loading = true;
    this.loadError = null;
    this.packages = [];
    this.pricingSource = {};

    if (!this.previewId) {
      this.loading = false;
      this.loadError = "Unable to load subscription pricing.";
      return;
    }

    const readyData = this.getReadyPricingSource();
    if (readyData && hasPaymentSettingPricing(readyData)) {
      this.applyPricingSource(readyData);
      return;
    }

    this.loadPaymentSettingFromApi(readyData || {});
  }

  private getReadyPricingSource(): RenewalPricingSource | null {
    const tempData = this.previewTempDataService.getTempPreviewData(this.previewId);
    if (tempData && hasPaymentSettingPricing(tempData)) {
      return tempData;
    }

    const accessDetail = this.previewAccessService.getVerifiedDetail(this.previewId);
    if (accessDetail && hasPaymentSettingPricing(accessDetail)) {
      return accessDetail;
    }

    return null;
  }

  private loadPaymentSettingFromApi(previewData: any): void {
    const digitalMemoryLogId = Number((previewData && previewData.id) || this.previewId);

    if (!digitalMemoryLogId || !Number.isFinite(digitalMemoryLogId)) {
      this.finishWithoutPricing();
      return;
    }

    // Public subscription page: skip auth interceptors.
    this.paymentSettingService.getByDigitalMemoryLogId(digitalMemoryLogId, false).subscribe(
      setting => {
        if (setting && hasPaymentSettingPricing({ paymentSetting: setting })) {
          this.applyPricingSource(
            Object.assign({}, previewData || {}, {
              paymentSetting: setting,
            })
          );
          return;
        }

        this.finishWithoutPricing();
      },
      () => {
        this.finishWithoutPricing();
      }
    );
  }

  private applyPricingSource(source: RenewalPricingSource): void {
    this.pricingSource = source || {};
    this.packages = buildSubscriptionPackages(this.pricingSource);
    this.loading = false;
    this.loadError = this.packages.length ? null : "Subscription pricing is not available.";
  }

  private finishWithoutPricing(): void {
    this.pricingSource = {};
    this.packages = [];
    this.loading = false;
    this.loadError = "Subscription pricing is not available.";
  }
}
